import React, { useState, useEffect } from 'react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin, Banknote, Wallet } from 'lucide-react';

const AtmWithdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [utilizzatori, setUtilizzatori] = useState([]);
  const [yearlyStats, setYearlyStats] = useState({ total: 0, by_user: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  const currentYear = new Date().getFullYear();
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    user_id: '',
    location: '',
    amount: '',
    bank: '',
    latitude: null,
    longitude: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [withdrawalsRes, utilizzatoriRes, statsRes] = await Promise.all([
        api.get('/withdrawals'),
        api.get('/utilizzatori'),
        api.get(`/withdrawals/stats/yearly?year=${currentYear}`),
      ]);
      
      setWithdrawals(withdrawalsRes.data);
      setUtilizzatori(utilizzatoriRes.data);
      setYearlyStats(statsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalizzazione non supportata dal browser');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({
          ...prev,
          latitude,
          longitude,
        }));
        
        // Try to get address from coordinates using reverse geocoding
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          if (data.display_name) {
            setFormData(prev => ({
              ...prev,
              location: data.display_name.split(',').slice(0, 3).join(','),
            }));
          }
        } catch (error) {
          console.log('Reverse geocoding failed, using coordinates');
          setFormData(prev => ({
            ...prev,
            location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
          }));
        }
        
        setGettingLocation(false);
        toast.success('Posizione rilevata');
      },
      (error) => {
        setGettingLocation(false);
        toast.error('Impossibile ottenere la posizione: ' + error.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        date: item.date.split('T')[0],
        user_id: item.user_id,
        location: item.location,
        amount: item.amount.toString(),
        bank: item.bank,
        latitude: item.latitude,
        longitude: item.longitude,
      });
    } else {
      setEditingItem(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        user_id: '',
        location: '',
        amount: '',
        bank: '',
        latitude: null,
        longitude: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_id) {
      toast.error('Seleziona una persona');
      return;
    }
    
    const payload = {
      ...formData,
      amount: parseFloat(formData.amount),
      date: new Date(formData.date).toISOString(),
    };

    try {
      if (editingItem) {
        await api.put(`/withdrawals/${editingItem.id}`, payload);
        toast.success('Prelievo aggiornato con successo');
      } else {
        await api.post('/withdrawals', payload);
        toast.success('Prelievo aggiunto con successo');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/withdrawals/${itemToDelete.id}`);
      toast.success('Prelievo eliminato con successo');
      fetchData();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getUserName = (userId) => {
    const user = utilizzatori.find(u => u.id === userId);
    return user ? user.name : 'Sconosciuto';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-600">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="atm-withdrawals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Prelievi ATM
          </h2>
          <p className="text-slate-600">Gestisci i prelievi dal bancomat</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
          data-testid="add-withdrawal-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Prelievo
        </Button>
      </div>

      {/* Stats Card */}
      <div className="bg-gradient-to-br from-[#61CE70] to-[#4CAF50] rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm mb-1">Saldo Prelievi {currentYear}</p>
            <p className="text-4xl font-bold" style={{ fontFamily: 'Manrope' }} data-testid="yearly-total">
              {formatCurrency(yearlyStats.total)}
            </p>
            <p className="text-white/60 text-sm mt-2">
              {yearlyStats.count} prelievi effettuati
            </p>
          </div>
          <div className="bg-white/20 p-4 rounded-full">
            <Wallet className="w-10 h-10" />
          </div>
        </div>
        
        {/* Stats by user */}
        {yearlyStats.by_user.length > 0 && (
          <div className="mt-6 pt-4 border-t border-white/20">
            <p className="text-white/80 text-sm mb-3">Per persona:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {yearlyStats.by_user.map((stat) => (
                <div key={stat.user_id} className="bg-white/10 rounded-lg p-3">
                  <p className="text-white/70 text-xs">{stat.user_name}</p>
                  <p className="text-lg font-semibold">{formatCurrency(stat.amount)}</p>
                  <p className="text-white/50 text-xs">{stat.count} prelievi</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Withdrawals List */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900">Lista Prelievi</h3>
        </div>
        
        {withdrawals.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Banknote className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nessun prelievo registrato</p>
            <p className="text-sm">Aggiungi il primo prelievo!</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {withdrawals.map((withdrawal) => (
              <div
                key={withdrawal.id}
                className="p-4 hover:bg-slate-50 transition-colors"
                data-testid={`withdrawal-${withdrawal.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-semibold text-slate-900">
                        {formatCurrency(withdrawal.amount)}
                      </span>
                      <span className="text-sm text-slate-500">
                        {formatDate(withdrawal.date)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                      <span className="bg-slate-100 px-2 py-1 rounded-md">
                        {getUserName(withdrawal.user_id)}
                      </span>
                      <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md">
                        {withdrawal.bank}
                      </span>
                      {withdrawal.location && (
                        <span className="flex items-center gap-1 text-slate-500">
                          <MapPin className="w-3 h-3" />
                          {withdrawal.location.length > 40 
                            ? withdrawal.location.substring(0, 40) + '...' 
                            : withdrawal.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(withdrawal)}
                      className="text-slate-600 hover:text-[#D94E1F] hover:bg-[#D94E1F]/10"
                      data-testid={`edit-withdrawal-${withdrawal.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setItemToDelete(withdrawal);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-slate-600 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-withdrawal-${withdrawal.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col bg-white" data-testid="withdrawal-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-slate-900">
              {editingItem ? 'Modifica Prelievo' : 'Nuovo Prelievo'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {editingItem ? 'Modifica i dettagli del prelievo' : 'Inserisci i dettagli del prelievo'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-3 py-2 px-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="date" className="text-slate-700 text-sm">Data *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl"
                    required
                    data-testid="withdrawal-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="amount" className="text-slate-700 text-sm">Importo (€) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl"
                    required
                    placeholder="0.00"
                    data-testid="withdrawal-amount-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="user_id" className="text-slate-700 text-sm">Persona *</Label>
                <Select
                  value={formData.user_id}
                  onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                >
                  <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="withdrawal-user-select">
                    <SelectValue placeholder="Seleziona persona" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {utilizzatori.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {utilizzatori.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    Nessun utilizzatore. Aggiungine uno nelle Impostazioni.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="bank" className="text-slate-700 text-sm">Banca *</Label>
                <Input
                  id="bank"
                  value={formData.bank}
                  onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl"
                  required
                  placeholder="es. Intesa Sanpaolo, UniCredit..."
                  data-testid="withdrawal-bank-input"
                />
              </div>

              <div>
                <Label htmlFor="location" className="text-slate-700 text-sm">Luogo</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Indirizzo o posizione"
                    className="flex-1 h-12 rounded-xl"
                    data-testid="withdrawal-location-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGetLocation}
                    disabled={gettingLocation}
                    className="shrink-0 h-12 px-4 rounded-xl"
                    data-testid="get-location-button"
                  >
                    <MapPin className={`w-4 h-4 ${gettingLocation ? 'animate-pulse' : ''}`} />
                  </Button>
                </div>
                {formData.latitude && formData.longitude && (
                  <p className="text-xs text-slate-500 mt-1">
                    📍 {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col gap-2 pt-4 border-t border-slate-100 mt-2">
              <Button
                type="submit"
                className="w-full h-12 bg-[#61CE70] hover:bg-[#4CAF50] text-white rounded-xl text-base font-medium"
                data-testid="save-withdrawal-button"
              >
                {editingItem ? 'Salva' : 'Aggiungi'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                className="w-full h-12 rounded-xl text-base"
                data-testid="cancel-withdrawal-button"
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white max-w-sm mx-4" data-testid="delete-withdrawal-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900 text-xl font-bold">Sei sicuro?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 text-base">
              Questa azione non può essere annullata. Il prelievo verrà eliminato definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
            <AlertDialogAction
              onClick={handleDelete}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl text-base font-medium"
              data-testid="confirm-delete-withdrawal"
            >
              Elimina
            </AlertDialogAction>
            <AlertDialogCancel 
              className="w-full h-12 border-slate-300 text-slate-700 rounded-xl text-base font-medium mt-0"
              data-testid="cancel-delete-withdrawal"
            >
              Annulla
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AtmWithdrawals;
